import { Inject, Injectable } from '@angular/core';
import { AppConfig, LanguageCode, LanguageCodeUpper, Replace } from '@digilize/shared/definitions/src';
import { Multilingual } from '@digilize/shared/definitions/src/lib/interfaces/Multilingual';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';
import { LangDefinition, TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  localeEvent = new Subject<LanguageCode>();
  langToChooseFrom$ = new BehaviorSubject(this.appConfig.language.languages);
  constructor(private translocoService: TranslocoService, @Inject(APP_CONFIG) private appConfig: AppConfig) {}

  /**
   * `useStoredLang: false` skips the `lang` record and takes the browser's language every time.
   * The record is per origin, and on the ticket origin the webshop, the portal (`/user/`) and
   * order2seat (`/order2seat/`) share it - a language picked in one of the others used to decide the
   * webshop's language for good, since the webshop has no switcher of its own to correct it
   * (PAUL-3519).
   */
  init(options: { useStoredLang?: boolean } = {}) {
    const { useStoredLang = true } = options;
    this.setLang(useStoredLang ? this.tryGetLangFromStorage() : this.getBrowserLangOrDefault());
  }

  // initLazyLoadedModule(translateRef: TranslateService) {
  //   /* fixes problem described here https://github.com/ngx-translate/core/issues/1193*/
  //   /* lib has no emitter specific emitter for translation loaded, so we wait for langChange to be sure new translations have been loaded -  */
  //   translateRef.onLangChange.pipe(take(1)).subscribe((el) => {
  //     this.isServiceInitialized || this.init();
  //     this.resetTranslations(translateRef);
  //     translateRef.setDefaultLang(this.tryGetLangFromStorage());
  //   });

  //   setTimeout(() => {
  //     this.isServiceInitialized || this.init();
  //     this.resetTranslations(translateRef);
  //   }, 500);
  //   /* this covers language change case*/
  //   this.localeEvent.subscribe((locale) => translateRef.use(locale));
  // }

  // private resetTranslations(translateRef: TranslateService) {
  //   const currentLang = translateRef.currentLang;
  //   translateRef.currentLang = '';
  //   translateRef.use(currentLang);
  // }

  setLangAndSave(lang: LanguageCode | LanguageCodeUpper) {
    const langLower = lang.toLowerCase() as LanguageCode;
    this.setLang(langLower);
    localStorage.setItem('lang', langLower);
  }

  setLang(lang: LanguageCode) {
    this.translocoService.setDefaultLang(lang);
    this.translocoService.setActiveLang(lang);
    // <html lang> is static in index.html, so without this it keeps claiming whatever the markup shipped
    // — browsers then offer to translate a page that is already in the user's language, and screen
    // readers mispronounce it. Only for a lang we actually serve: an unsupported one would replace a
    // correct value with a language no translation exists for.
    if (this.isAvailableLang(lang)) {
      document.documentElement.lang = lang;
      this.removeNotranslateMeta();
    }
    this.localeEvent.next(lang);
    this.setLangCookie(lang);
  }

  private removeNotranslateMeta() {
    document.querySelector('meta[name="google"][content="notranslate"]')?.remove();
  }

  private isAvailableLang(lang: LanguageCode): boolean {
    /* availableLangs is either plain codes or {id, label} objects, depending on how the app configured it */
    const available = this.translocoService.getAvailableLangs() as (string | LangDefinition)[];
    return available.some((el) => (typeof el === 'string' ? el : el.id) === lang);
  }

  public setCookie(name: string, value: string, expireDays: number, path: string = '') {
    const d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    const cpath = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath}; SameSite=Lax`;
  }

  setLangCookie(lang: LanguageCode) {
    const mappings: Record<string, string> = {
      en: 'en-US',
      pl: 'pl-PL',
      de: 'de-DE',
      fr: 'fr-FR',
      nl: 'nl-NL',
      es: 'es-ES',
      pt: 'pt-BR',
      ru: 'ru-RU',
      zh: 'zh-CN',
      ja: 'ja-JP',
    };

    const culture = mappings[lang] || 'en-US';
    document.cookie = `.AspNetCore.Culture=c=${culture}|uic=${culture}; Path=/;`;
  }

  getTextFromMultilingual(multi: Multilingual, replace?: Replace): string {
    if (!multi) return '';
    const activeLang = this.translocoService.getActiveLang();
    const text = multi[this.translocoService.getActiveLang() as keyof Multilingual];
    return replace ? text.replace(replace.searchValue, replace.replaceValue) : text;
  }

  tryGetLangFromStorage(): LanguageCode {
    if (localStorage.getItem('lang')) {
      const langCodeFromStorage = localStorage.getItem('lang') as LanguageCode;
      /* we are checking if lang from localstorage exists in app.config (other localhost projects might have wider variety of langs)  */
      return this.appConfig.language.languages.find((lang) => lang.code === langCodeFromStorage)
        ? langCodeFromStorage
        : this.appConfig.language.defaultLanguage;
    }
    return this.getBrowserLangOrDefault();
  }

  private getBrowserLangOrDefault(): LanguageCode {
    const langListLower = this.appConfig.language.languages.map((lang) => lang.code.toLowerCase());
    const browserLanguage = this.getFirstBrowserLanguage();
    return langListLower.includes(browserLanguage)
      ? browserLanguage
      : (this.appConfig.language.defaultLanguage.toLowerCase() as LanguageCode);
  }
  private getFirstBrowserLanguage() {
    let nav = window.navigator,
      browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'],
      i,
      language;

    // support for HTML 5.1 "navigator.languages"
    if (Array.isArray(nav.languages)) {
      for (i = 0; i < nav.languages.length; i++) {
        language = nav.languages[i];
        if (language && language.length) {
          return language.split('-')[0];
        }
      }
    }

    // support for other well known properties in browsers
    for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
      language = nav[browserLanguagePropertyKeys[i]];
      if (language && language.length) {
        return language.split('-')[0];
      }
    }

    return null;
  }

  updateLangToChooseFrom(toChooseFrom: LanguageCode[] | LanguageCodeUpper[]) {
    /* toChooseFrom languages must exist in appConfig providers*/
    const newLangs = this.appConfig.language.languages.filter((lang) => {
      return toChooseFrom.map((from) => from.toLowerCase() as LanguageCode).find((from) => from === lang.code);
    });
    this.langToChooseFrom$.next(newLangs);
  }
}
